import { isEqual } from 'lodash'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import ConfirmModal from '@/components/ConfirmModal'
import FormField from '@/components/FormField'
import Input from '@/components/Input/Input'
import Select from '@/components/Select/Select'
import { FormSectionFields } from '@/components/common/FormGrid/FormGrid'
import { useAllEducationLevels } from '@/queries/useAllEducationLevels'
import type { FormFieldType } from '@/types/form-field-type'

import styles from './BasicInformation.module.scss'
import {
  CURRICULUM_FORM_DEFAULT_VALUES,
  type CurriculumForm
} from '../../hooks/useCurriculumForm'
import { getCurriculumAimOptions } from '../../utils/get-curriculum-aim-options'

const BasicInformation = () => {
  const [educationLevelModal, setEducationLevelModal] = useState<string | null>(
    null
  )
  const form = useFormContext<CurriculumForm>()
  const { t } = useTranslation('curriculumsManagement')
  const { data: educationLevels, isLoading } = useAllEducationLevels()

  const createEducationLevelChangeHandler =
    (inputProps: FormFieldType<string>): FormFieldType<string>['onChange'] =>
    value => {
      const isDefaultBasketValue = isEqual(
        form.getValues('baskets'),
        CURRICULUM_FORM_DEFAULT_VALUES.baskets
      )
      const didValueChange = form.getValues('educationLevel') !== value

      if (didValueChange && !isDefaultBasketValue) {
        setEducationLevelModal(value)
      } else {
        inputProps.onChange?.(value)
      }
    }

  const closeEducationModal = () => setEducationLevelModal(null)
  const handleEducationLevelConfirm = () => {
    if (educationLevelModal === null) return

    form.setValue('educationLevel', educationLevelModal)
    form.setValue('baskets', CURRICULUM_FORM_DEFAULT_VALUES.baskets)
    closeEducationModal()
  }

  const aimOptions = getCurriculumAimOptions(t)

  return (
    <>
      <div className={styles.container}>
        <FormSectionFields>
          <FormField
            control={form.control}
            id="education-level"
            label={t('label.education-level')}
            required
            name="educationLevel"
            render={({ inputProps }) => (
              <Select
                placeholder={t('placeholder.select-education-level')}
                {...inputProps}
                options={educationLevels?.options ?? []}
                loading={isLoading}
                onChange={createEducationLevelChangeHandler(inputProps)}
              />
            )}
          />
          <FormField
            control={form.control}
            id="name"
            label={t('label.name')}
            labelTooltipText={t('tooltip.name-should-be-unique')}
            required
            name="name"
            render={({ inputProps }) => (
              <Input
                placeholder={t('placeholder.type-name')}
                maxLength={80}
                {...inputProps}
              />
            )}
          />
        </FormSectionFields>
        <FormSectionFields>
          <FormField
            control={form.control}
            id="aim"
            label={t('label.aim')}
            required
            name="aim"
            render={({ inputProps }) => (
              <Select
                placeholder={t('placeholder.select-aim')}
                {...inputProps}
                options={aimOptions}
              />
            )}
          />
        </FormSectionFields>
      </div>
      <ConfirmModal
        open={!!educationLevelModal}
        onOpenChange={closeEducationModal}
        header={t('header.change-education-level')}
        subheader={
          <>
            <span>{t('help.change-education-level')}</span>
            <span>{t('help.action-cannot-be-undone')}</span>
          </>
        }
        confirmButton={
          <Button variant="danger" onClick={handleEducationLevelConfirm}>
            {t('button.confirm')}
          </Button>
        }
      />
    </>
  )
}

export default BasicInformation
